import { IconWrapper, PrimaryButton, pxToRem, pxTovW } from '@geneo2-web/shared-ui';
import { Box, Typography } from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';

// Same card shell (root/icon-box sizing, radii, shadow, badge/avatar row,
// divider, footer row) as AssessInfoCard's homework cards — reads as the
// same kind of card on Home, not a different product, plus a diagonal
// "LIVE" ribbon since nothing else on the card otherwise says so once it
// no longer has its own distinct hero treatment. Not literally AssessInfoCard
// itself: that component is hard-wired to StudentAssessmentTask (score,
// attempt status, poster image, deadline...), none of which a live quiz
// session — still being joined, no attempt yet — actually has.
interface IProps {
  joining: boolean;
  onJoin: () => void;
  taskTitle?: string;
  teacherName?: string;
  subject?: string;
  className?: string;
  sectionName?: string;
  totalQuestions?: number;
}
export const LiveQuizJoinCard = (props: IProps) => {
  const {
    joining,
    onJoin,
    taskTitle,
    teacherName,
    subject,
    className,
    sectionName,
    totalQuestions,
  } = props;
  const gradeSection = [className, sectionName].filter(Boolean).join(' ');
  const teacherInitial = teacherName?.trim()?.[0]?.toUpperCase();

  return (
    <Box
      sx={{
        position: 'relative',
        height: { xs: pxToRem(148), md: pxTovW(280), lg: pxTovW(189) },
        width: { xs: '100%', md: pxTovW(690), lg: pxTovW(464) },
        borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
        backgroundColor: '#FFFFFF',
        border: '1px solid #E0DFDE',
        padding: {
          xs: pxToRem(8),
          md: `${pxTovW(18)} ${pxTovW(15)}`,
          lg: `${pxTovW(13)} ${pxTovW(12)}`,
        },
        display: 'flex',
        alignItems: 'center',
        gap: { xs: pxToRem(15), md: pxTovW(15), lg: pxTovW(15) },
        boxSizing: 'border-box',
        boxShadow: `0px 0px ${pxToRem(11)} #E7E7E7D9`,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* Diagonal corner ribbon — its own clipped box rather than relying on
          the card's overflow:hidden alone, so the strip's rotated corners
          never poke out past a rounded card edge. */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: { xs: pxToRem(72), md: pxTovW(90) },
          height: { xs: pxToRem(72), md: pxTovW(90) },
          overflow: 'hidden',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: { xs: pxToRem(14), md: pxTovW(18) },
            left: { xs: pxToRem(-22), md: pxTovW(-26) },
            width: { xs: pxToRem(100), md: pxTovW(125) },
            transform: 'rotate(-45deg)',
            transformOrigin: 'center',
            background: 'linear-gradient(90deg, #FF3B6E 0%, #FF6FB0 100%)',
            boxShadow: '0px 2px 4px rgba(0,0,0,0.25)',
            textAlign: 'center',
            py: { xs: pxToRem(2), md: pxTovW(3) },
          }}
        >
          <Typography
            variant="smallestText"
            fontWeight={700}
            sx={{ color: '#FFFFFF', letterSpacing: 1 }}
          >
            LIVE
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: { xs: pxToRem(6), md: pxTovW(8) },
          height: { xs: '100%', md: '100%', lg: pxTovW(157) },
          width: { xs: pxToRem(103), md: pxTovW(221), lg: pxTovW(157) },
          minWidth: { xs: pxToRem(103), md: pxTovW(221), lg: pxTovW(157) },
          boxSizing: 'border-box',
          borderRadius: { xs: pxToRem(16), md: pxTovW(16) },
          background: 'linear-gradient(135deg, #7C4DFF 0%, #B36BFF 60%, #FF6FB0 100%)',
        }}
      >
        <Box
          sx={{
            width: { xs: pxToRem(40), md: pxTovW(44) },
            height: { xs: pxToRem(40), md: pxTovW(44) },
            borderRadius: '50%',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: { xs: pxToRem(28), md: pxTovW(30) },
              height: { xs: pxToRem(28), md: pxTovW(30) },
              borderRadius: '50%',
              backgroundColor: '#7C4DFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
            }}
          >
            <SensorsIcon sx={{ fontSize: { xs: pxToRem(16), md: pxTovW(18) } }} />
            <Box
              sx={{
                position: 'absolute',
                top: -1,
                right: -1,
                width: 9,
                height: 9,
                borderRadius: '50%',
                backgroundColor: '#FF5B94',
                border: '2px solid #FFFFFF',
                animation: 'live-quiz-join-pulse 1.4s ease-in-out infinite',
                '@keyframes live-quiz-join-pulse': {
                  '0%': { transform: 'scale(0.85)', opacity: 1 },
                  '50%': { transform: 'scale(1.15)', opacity: 0.6 },
                  '100%': { transform: 'scale(0.85)', opacity: 1 },
                },
              }}
            />
          </Box>
        </Box>
        <Typography
          variant="smallestText"
          fontWeight={700}
          sx={{ color: '#FFFFFF' }}
        >
          Live Quiz
        </Typography>
      </Box>

      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          justifyContent: 'space-between',
          minWidth: '0',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: { xs: pxToRem(3), md: pxTovW(4) },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: pxToRem(10), md: pxTovW(16) },
              marginTop: { xs: pxToRem(2), md: pxTovW(10), lg: pxTovW(5) },
              marginBottom: { xs: pxToRem(1), md: pxTovW(5) },
            }}
          >
            {teacherInitial && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: pxToRem(5), md: pxTovW(5) },
                }}
              >
                <Box
                  sx={{
                    width: { xs: pxToRem(17), md: pxTovW(34), lg: pxTovW(24) },
                    height: { xs: pxToRem(17), md: pxTovW(34), lg: pxTovW(24) },
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="smallestText"
                    fontWeight={700}
                    sx={{ color: 'common.white' }}
                  >
                    {teacherInitial}
                  </Typography>
                </Box>
                <Typography variant="smallText" fontWeight="regular">
                  {teacherName}
                </Typography>
              </Box>
            )}
          </Box>

          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordWrap: 'break-word',
            }}
          >
            {taskTitle || 'Geneo Live Quiz'}
          </Typography>

          {(subject || gradeSection) && (
            <Typography
              variant="subText"
              fontWeight={700}
              color="primary.main"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {[subject, gradeSection].filter(Boolean).join(' · ')}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            borderTop: '1px solid #E0E0E0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: { xs: pxToRem(6), md: pxTovW(8) },
          }}
        >
          {typeof totalQuestions === 'number' ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: pxToRem(3), md: pxTovW(3) },
              }}
            >
              <Box sx={{ display: 'flex', gap: { xs: pxToRem(4), md: pxTovW(4) }, alignItems: 'center' }}>
                <IconWrapper name="questions" size="small" parentFolder="icons" type="png" />
                <Typography variant="smallText">{totalQuestions}</Typography>
              </Box>
              <Typography variant="smallestText">Questions</Typography>
            </Box>
          ) : (
            <Box />
          )}

          <PrimaryButton
            onClick={onJoin}
            disabled={joining}
            sx={{
              width: { xs: pxToRem(110), md: pxTovW(160) },
              height: { xs: pxToRem(32), md: pxTovW(40) },
            }}
          >
            <Typography variant="smallText" color="common.white" fontWeight="bold">
              {joining ? 'Joining…' : 'Join Now'}
            </Typography>
          </PrimaryButton>
        </Box>
      </Box>
    </Box>
  );
};

export default LiveQuizJoinCard;
